import React, { createContext, useContext, useEffect, useState } from 'react';
import { AuthState } from '../types/user';
import { supabase } from '../lib/supabase';
import { Session } from '@supabase/supabase-js';

interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
  });

  useEffect(() => {
    // Verificar sessão atual
    supabase.auth.getSession().then(({ data: { session } }) => {
      handleSession(session);
    });

    // Escutar mudanças de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Tratar eventos específicos
      switch (event) {
        case 'SIGNED_OUT':
          setState({ user: null, session: null, loading: false });
          break;
        case 'TOKEN_REFRESHED':
          handleSession(session);
          break;
        case 'SIGNED_IN':
          handleSession(session);
          break;
        case 'PASSWORD_RECOVERY':
          // A sessão de recuperação serve APENAS para trocar a senha em
          // /redefinir-senha (que lê a sessão direto do supabase). Não a
          // promovemos a login: quem tem só acesso ao e-mail não deve navegar
          // pelo sistema. As rotas privadas continuam exigindo login normal.
          setState({ user: null, session: null, loading: false });
          break;
        default:
          handleSession(session);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function handleSession(session: Session | null) {
    if (session) {
      // Buscar dados adicionais do usuário
      const { data: userData, error } = await supabase
        .from('usuarios')
        .select('*')
        .eq('id', session.user.id)
        .single();

      if (error) {
        console.error('Erro ao buscar dados do usuário:', error);
        // Sem esse setState o loading ficava true para sempre e a aplicação
        // travava no spinner até um reload manual.
        setState({ user: null, session: null, loading: false });
        return;
      }

      // Verificar se o usuário está bloqueado
      if (userData.ativo === false) {
        await signOut();
        return;
      }

      setState({
        user: {
          id: session.user.id,
          email: session.user.email!,
          nome: userData.nome,
          tipo: userData.tipo,
          ativo: userData.ativo,
          id_revenda: userData.id_revenda,
        },
        session,
        loading: false,
      });
    } else {
      setState({ user: null, session: null, loading: false });
    }
  }

  async function signIn(email: string, password: string) {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Traduzindo mensagens comuns do Supabase
        const mensagens: { [key: string]: string } = {
          'Invalid login credentials': 'Email ou senha incorretos',
          'Email not confirmed': 'Email não confirmado',
          'Invalid email': 'Email inválido',
          'User not found': 'Usuário não encontrado',
        };
        throw new Error(mensagens[error.message] || 'Erro ao fazer login');
      }

      // Verificar se o usuário está bloqueado
      const { data: session } = await supabase.auth.getSession();
      if (session.session) {
        const { data: userData, error: userError } = await supabase
          .from('usuarios')
          .select('ativo')
          .eq('id', session.session.user.id)
          .single();

        if (userError) {
          throw new Error('Erro ao verificar status do usuário');
        }

        if (userData.ativo === false) {
          // Se o usuário estiver bloqueado, fazer logout e lançar erro
          await supabase.auth.signOut();
          throw new Error('Usuário bloqueado. Entre em contato com o administrador.');
        }
      }
    } catch (error) {
      console.error('Erro no login:', error);
      throw error;
    }
  }

  async function signOut() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw new Error('Erro ao sair do sistema');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      throw error;
    }
  }

  async function resetPassword(email: string) {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/redefinir-senha`,
      });

      if (error) {
        // Traduzindo mensagens comuns do Supabase
        const mensagens: { [key: string]: string } = {
          'User not found': 'Usuário não encontrado',
          'Email not confirmed': 'Email não confirmado',
          'Invalid email': 'Email inválido',
        };
        throw new Error(mensagens[error.message] || 'Erro ao enviar email de redefinição');
      }
    } catch (error) {
      console.error('Erro ao solicitar redefinição de senha:', error);
      throw error;
    }
  }

  async function updatePassword(password: string) {
    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        // Traduzindo mensagens comuns do Supabase. O texto original é mantido
        // como fallback: engolir o erro real foi o que escondeu, por muito
        // tempo, a falha de sessão ausente neste fluxo.
        const mensagens: { [key: string]: string } = {
          'Auth session missing!': 'Sessão de recuperação expirada. Solicite um novo link.',
          'New password should be different from the old password.':
            'A nova senha deve ser diferente da senha atual.',
          'Password should be at least 6 characters.':
            'A senha deve ter pelo menos 6 caracteres.',
        };
        throw new Error(mensagens[error.message] || error.message || 'Erro ao atualizar senha');
      }
    } catch (error) {
      console.error('Erro ao atualizar senha:', error);
      throw error;
    }
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        signIn,
        signOut,
        resetPassword,
        updatePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
} 